import type { GlobalAppSlug } from "@/lib/dashboardAppRegistry";
import {
  DASHBOARD_APP_ORDER,
  DASHBOARD_APP_BY_SLUG,
} from "@/lib/dashboardAppRegistry";

const ATW_PLATFORM_ORG_ID = "4133643";

const DASHBOARD_APP_ORDER_CORE: GlobalAppSlug[] = DASHBOARD_APP_ORDER.filter(
  (s): s is Exclude<GlobalAppSlug, "platform"> => s !== "platform",
);

const LEGACY_FULL_ONE_INNER: readonly Exclude<GlobalAppSlug, "home">[] = [
  "crm",
  "finance",
  "human-resources",
  "marketing",
  "knowledge",
  "projects",
  "custom-apps",
  "support",
];

export type OrgProductsResolved = {
  oneEnabled?: boolean;
  payEnabled?: boolean;
  platformEnabled?: boolean;
  worksuitePlan?: string | null;
};

function isAtwPlatformOrg(orgId: string | null | undefined): boolean {
  const o = orgId?.trim();
  if (!o || !/^\d{7}$/.test(o)) return false;
  return o === ATW_PLATFORM_ORG_ID;
}

function canAccessPlatformApp(
  dashboardOrgId: string | null | undefined,
  role: string | undefined,
): boolean {
  return role === "super_admin" && isAtwPlatformOrg(dashboardOrgId);
}

function expandStaleFullOneEntitlements(slugs: GlobalAppSlug[]): GlobalAppSlug[] {
  const set = new Set<GlobalAppSlug>(slugs);
  const hasLegacyFull = LEGACY_FULL_ONE_INNER.every((s) => set.has(s));
  if (hasLegacyFull) {
    set.add("analytics");
  }
  return DASHBOARD_APP_ORDER.filter((s) => set.has(s));
}

function applyOrgProductGates(
  slugs: GlobalAppSlug[],
  products: OrgProductsResolved | null | undefined,
): GlobalAppSlug[] {
  if (!products) return slugs;
  return slugs.filter((slug) => {
    if (slug === "worksuite") return products.worksuitePlan != null;
    if (slug === "platform") return products.platformEnabled;
    if (
      slug === "crm" ||
      slug === "finance" ||
      slug === "human-resources" ||
      slug === "marketing" ||
      slug === "knowledge" ||
      slug === "projects" ||
      slug === "analytics" ||
      slug === "custom-apps" ||
      slug === "support"
    ) {
      return products.oneEnabled;
    }
    return true;
  });
}

export function getRoleBaseDashboardAppSlugs(role: string | undefined): GlobalAppSlug[] {
  const r = role || "standard_user";

  if (
    r === "super_admin" ||
    r === "account_owner" ||
    r === "account_admin" ||
    r === "manager" ||
    r === "standard_user" ||
    r === "limited_user" ||
    r === "read_only" ||
    r === "admin" ||
    r === "editor" ||
    r === "author" ||
    r === "viewer"
  ) {
    return [...DASHBOARD_APP_ORDER_CORE];
  }

  if (r === "billing_admin") {
    return ["home", "finance", "marketing", "custom-apps"];
  }

  return [...DASHBOARD_APP_ORDER_CORE];
}

export function getEffectiveDashboardAppSlugs(
  role: string | undefined,
  appEntitlements: GlobalAppSlug[] | null | undefined,
  dashboardOrgId?: string | null,
  orgProducts?: OrgProductsResolved | null,
): GlobalAppSlug[] {
  const r = role || "standard_user";
  let out: GlobalAppSlug[];
  if (r === "super_admin" || r === "account_owner") {
    out = [...DASHBOARD_APP_ORDER_CORE];
    if (r === "super_admin" && canAccessPlatformApp(dashboardOrgId, r)) {
      out.push("platform");
    }
  } else if (appEntitlements === undefined || appEntitlements === null) {
    out = getRoleBaseDashboardAppSlugs(r);
  } else {
    const valid = new Set<string>(DASHBOARD_APP_ORDER);
    const chosen = expandStaleFullOneEntitlements(
      appEntitlements.filter((s): s is GlobalAppSlug => valid.has(s)),
    );
    const set = new Set<GlobalAppSlug>(["home", ...chosen]);
    out = DASHBOARD_APP_ORDER.filter((s) => {
      if (!set.has(s)) return false;
      if (s === "platform") return canAccessPlatformApp(dashboardOrgId, r);
      return true;
    });
  }
  return applyOrgProductGates(out, orgProducts);
}

export function userMayManageCustomApps(
  role: string | undefined,
  canManageCustomAppsFromProfile: boolean | undefined,
): boolean {
  if (role === "super_admin") return true;
  return canManageCustomAppsFromProfile === true;
}

export function firstAllowedAppHref(
  role: string | undefined,
  appEntitlements?: GlobalAppSlug[] | null,
  dashboardOrgId?: string | null,
  orgProducts?: OrgProductsResolved | null,
): string {
  const allowed = getEffectiveDashboardAppSlugs(role, appEntitlements, dashboardOrgId, orgProducts);
  for (const slug of DASHBOARD_APP_ORDER) {
    if (slug === "home") continue;
    if (allowed.includes(slug)) {
      return DASHBOARD_APP_BY_SLUG[slug].topNavHref;
    }
  }
  return "/";
}
